import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import {
  ArtifactType,
  GetProfileArtifactsQueryQuery,
  GetProfileArtifactsQueryQueryVariables,
} from "types/graphql-schema";
import { GetProfileQueryQuery, GoalState } from "types/graphql-schema";

import Artifact from "@apps/artifact/artifact";
import { getExplorerFiltersUrl } from "@apps/explorer/helpers";
import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import { assertEdgesNonNull } from "@helpers/helpers";

import ProfileContainer from "../../profile-container";
import ProfileEmptyState from "../../profile-empty-state";
import getProfileArtifactsQuery from "../graphql/get-profile-artifacts-query";
import ProfileReviews from "./profile-reviews";

const ProfileArtifacts = ({
  profile,
}: {
  profile: NonNullable<GetProfileQueryQuery["user"]>;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const currentUser = currentUserVar();
  const isAdmin = isAdminVar();
  const isManager = profile?.managersList.some(
    ({ id }) => id === currentUser.id,
  );

  const incompleteStates = compact(organization.actionItemStates)
    .filter((state) => !state.isComplete)
    .map((state) => state.value);

  const sections = compact([
    organization.featureFlags.goals && (
      <ProfileArtifactsContent
        key="goals"
        artifactType={ArtifactType.Goal}
        viewAllLink={`/goals/user/${profile.id}`}
        title={`Open ${label("goal", {
          titleCase: true,
          pluralize: true,
        })}`}
        variables={{
          goalOwners: [profile.id],
          goalStates: [GoalState.Open, GoalState.Draft],
          artifactType: ArtifactType.Goal,
        }}
      />
    ),
    organization.featureFlags.actionItems && (
      <ProfileArtifactsContent
        key="action-items"
        artifactType={ArtifactType.ActionItem}
        viewAllLink={getExplorerFiltersUrl({
          actionItemAssignee: profile.id,
          orderBy: "due_date",
          actionItemState: incompleteStates,
          type: ArtifactType.ActionItem,
        })}
        title={`${label("action item", {
          titleCase: true,
          pluralize: true,
        })}`}
        variables={{
          artifactType: ArtifactType.ActionItem,
          first: 5,
          actionItemIsComplete: false,
          actionItemAssignee: profile.id,
          orderBy: "due_date",
        }}
      />
    ),
    (isAdmin || isManager || profile.id === currentUser.id) && (
      <ProfileReviews profileId={profile.id} key="reviews" />
    ),
    organization.featureFlags.recognitions && (
      <ProfileArtifactsContent
        key="recognitions"
        artifactType={ArtifactType.Recognition}
        viewAllLink={`/recognition-board?recipientId=${profile.id}`}
        title={`${label("recognition", {
          titleCase: true,
          pluralize: true,
        })}`}
        variables={{
          recognitionRecipient: profile.id,
          artifactType: ArtifactType.Recognition,
          first: 5,
          orderBy: "-created",
        }}
      />
    ),
    organization.featureFlags.feedbacks && (
      <ProfileArtifactsContent
        key="feedbacks"
        artifactType={ArtifactType.Feedback}
        viewAllLink={`/feedbacks/received?userId=${profile.id}&dateRange=all`}
        title={`${label("feedback", {
          titleCase: true,
          pluralize: true,
        })}`}
        variables={{
          artifactType: ArtifactType.Feedback,
          first: 5,
          feedbackRecipient: profile.id,
          orderBy: "-created",
        }}
      />
    ),
  ]);

  return sections;
};

const ProfileArtifactsContent = ({
  artifactType,
  viewAllLink,
  title,
  variables,
}: {
  artifactType: ArtifactType;
  title: string;
  viewAllLink: string;
  variables: GetProfileArtifactsQueryQueryVariables;
}) => {
  const label = useLabel();
  const { data, loading, fetchMore } = useQuery<
    GetProfileArtifactsQueryQuery,
    GetProfileArtifactsQueryQueryVariables
  >(getProfileArtifactsQuery, {
    variables: variables,
  });

  const artifacts = data?.artifacts ? assertEdgesNonNull(data.artifacts) : [];

  const handleClickLoadMore = () => {
    if (data?.artifacts?.pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: data?.artifacts?.pageInfo?.endCursor,
          merge: true,
        },
      });
    }
  };

  return (
    <>
      <ProfileContainer
        title={title}
        topRightContent={
          viewAllLink ? (
            <AppLink
              to={viewAllLink}
              className="text-blue-link hover:underline"
            >
              View All{" "}
              {label(artifactType, { pluralize: true, titleCase: true })}
            </AppLink>
          ) : null
        }
      >
        {!data && loading ? (
          <Loading>Loading...</Loading>
        ) : artifacts.length > 0 ? (
          <div className="flex flex-col gap-4 w-full">
            {artifacts.map((artifact) => (
              <Artifact
                key={artifact.id}
                artifact={artifact}
                hideActionDropdown
              />
            ))}
            {data?.artifacts?.pageInfo?.hasNextPage && (
              <div className="flex justify-center gap-2">
                <Button
                  size={ButtonSize.medium}
                  text={loading ? "loading..." : "load more"}
                  onClick={handleClickLoadMore}
                  disabled={loading}
                />
              </div>
            )}
          </div>
        ) : (
          <ProfileEmptyState>
            No {label(artifactType, { pluralize: true })}.
          </ProfileEmptyState>
        )}
      </ProfileContainer>
    </>
  );
};

export default ProfileArtifacts;
